<template>
  <div class="container mx-auto px-4 py-8 bg-gray-50">
    <!-- Header -->
    <div class="mb-8">
      <h1 class="text-3xl font-bold mb-6 text-center text-customHafadhColor">تقرير جلسات التعاهد</h1>
      
      <!-- Date Filter -->
      <div class="flex justify-center gap-4 items-center flex-wrap">
        <label class="text-lg font-semibold text-gray-700">اختر التاريخ:</label>
        <input
          type="date"
          v-model="selectedDate"
          @change="fetchSessions"
          class="block px-4 py-2 border-2 border-customHafadhColor rounded-lg focus:outline-none focus:ring-2 focus:ring-customHafadhColor"
        />
        <button
          @click="fetchSessions"
          class="px-4 py-2 bg-customHafadhColor text-white rounded-lg hover:bg-customHafadhColor/90 transition-colors duration-300"
          :disabled="loading"
        >
          <i class="fas fa-search ml-2"></i>
          بحث
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-8">
      <div class="inline-block animate-spin rounded-full h-12 w-12 border-4 border-customHafadhColor border-t-transparent"></div>
      <p class="mt-4 text-lg text-customHafadhColor">جاري التحميل...</p>
    </div>

    <!-- Error State -->
    <div v-if="error" class="bg-red-100 border-2 border-red-400 text-red-700 px-6 py-4 rounded-lg relative text-right mb-4">
      <strong class="font-bold ml-2">خطأ!</strong>
      <span class="block sm:inline">{{ error }}</span>
    </div>

    <!-- Sessions Table -->
    <div v-if="!loading && !error && sessions.length > 0" class="bg-white rounded-lg shadow-lg overflow-hidden">
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-customHafadhColor text-white">
            <tr>
              <th class="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider">#</th>
              <th class="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider">منشئ الجلسة</th>
              <th class="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider">الدارس المشارك</th>
              <th class="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider">التاريخ</th>
              <th class="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider">الوقت</th>
              <th class="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider">الحالة</th>
              <th class="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider">تم الانضمام</th>
              <th class="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider">التسجيل</th>
              <th class="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider">الإجراءات</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="(session, index) in sessions" :key="session.id" class="hover:bg-gray-50">
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ index + 1 }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {{ session.initiator_student?.full_name || 'غير معروف' }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {{ session.partner_student?.full_name || 'لم يتم الانضمام بعد' }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ session.session_date }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {{ formatTime(session.session_time_from) }} - {{ formatTime(session.session_time_to) }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span :class="getStatusClass(session.status)" class="px-2 py-1 text-xs font-semibold rounded-full">
                  {{ session.status_ar }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                <div v-if="session.trackers && session.trackers.length > 0">
                  <div v-for="tracker in session.trackers" :key="tracker.id" class="mb-1">
                    <div class="font-semibold">{{ tracker.student_name }}</div>
                    <div class="text-xs text-gray-500" v-if="tracker.student_punch_in">
                      دخول: {{ formatDateTime(tracker.student_punch_in) }}
                    </div>
                    <div class="text-xs text-gray-500" v-if="tracker.student_punch_out">
                      خروج: {{ formatDateTime(tracker.student_punch_out) }}
                    </div>
                  </div>
                </div>
                <span v-else class="text-gray-400">لم يتم الانضمام</span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm">
                <span v-if="session.has_recording" class="text-green-600 font-semibold">
                  <i class="fas fa-check-circle ml-1"></i>
                  متاح
                </span>
                <span v-else class="text-gray-400">
                  <i class="fas fa-times-circle ml-1"></i>
                  غير متاح
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm">
                <button
                  v-if="session.has_recording"
                  @click="playRecording(session)"
                  class="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors duration-300"
                >
                  <i class="fas fa-play ml-1"></i>
                  استمع
                </button>
                <span v-else class="text-gray-400">-</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Empty State -->
    <div v-if="!loading && !error && sessions.length === 0" class="text-center py-12 text-gray-500 text-lg">
      <i class="fas fa-calendar-times text-4xl mb-4 text-gray-400"></i>
      <p>لا توجد جلسات تعاهد في هذا التاريخ</p>
    </div>

    <!-- Audio Player Modal -->
    <div v-if="showAudioPlayer && currentRecordingUrl" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" @click.self="closeAudioPlayer">
      <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-xl font-bold text-customHafadhColor">استماع التسجيل</h3>
          <button @click="closeAudioPlayer" class="text-gray-500 hover:text-gray-700">
            <i class="fas fa-times text-xl"></i>
          </button>
        </div>
        <div class="mb-4">
          <p class="text-sm text-gray-600 mb-2">
            <strong>منشئ الجلسة:</strong> {{ currentSession?.initiator_student?.full_name }}
          </p>
          <p class="text-sm text-gray-600 mb-2" v-if="currentSession?.partner_student">
            <strong>الدارس المشارك:</strong> {{ currentSession.partner_student.full_name }}
          </p>
          <p class="text-sm text-gray-600">
            <strong>التاريخ:</strong> {{ currentSession?.session_date }} 
            <strong class="mr-4">الوقت:</strong> {{ formatTime(currentSession?.session_time_from) }} - {{ formatTime(currentSession?.session_time_to) }}
          </p>
        </div>
        <audio
          ref="audioPlayer"
          :src="currentRecordingUrl"
          controls
          class="w-full"
          @error="handleAudioError"
        >
          متصفحك لا يدعم تشغيل الصوت.
        </audio>
        <div v-if="audioError" class="mt-4 text-red-600 text-sm">
          <i class="fas fa-exclamation-triangle ml-1"></i>
          {{ audioError }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import url from '@/url';
import axios from 'axios';

export default {
  name: 'PeerReviewReport',
  data() {
    return {
      sessions: [],
      selectedDate: new Date().toISOString().split('T')[0],
      loading: false,
      error: null,
      showAudioPlayer: false,
      currentRecordingUrl: null,
      currentSession: null,
      audioError: null,
    };
  },
  mounted() {
    this.fetchSessions();
  },
  methods: {
    async fetchSessions() {
      this.loading = true;
      this.error = null;

      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          process.env.VUE_APP_SERVICE_URL + url.peer_review_report + `?date=${this.selectedDate}`,
          {
            headers: {
              'X-Authorization': token,
              'Content-Type': 'application/json',
            },
          }
        );

        if (response.data?.result?.status === 200) {
          this.sessions = response.data.result.data || [];
        } else {
          this.error = response.data?.result?.mesAr || 'فشل في جلب الجلسات';
        }
      } catch (error) {
        console.error('Error fetching sessions:', error);
        this.error = error.response?.data?.result?.mesAr || 'حدث خطأ أثناء جلب الجلسات';
      } finally {
        this.loading = false;
      }
    },
    async playRecording(session) {
      this.audioError = null;
      this.currentSession = session;

      // Find tracker with recording
      const trackerWithRecording = session.trackers?.find(t => t.has_recording);
      
      if (!trackerWithRecording) {
        this.error = 'لا يوجد تسجيل متاح لهذه الجلسة';
        return;
      }

      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          process.env.VUE_APP_SERVICE_URL + url.peer_review_recording + `/${trackerWithRecording.id}`,
          {
            headers: {
              'X-Authorization': token,
              'Content-Type': 'application/json',
            },
          }
        );

        if (response.data?.result?.status === 200) {
          this.currentRecordingUrl = response.data.result.data.downloadUrl;
          this.showAudioPlayer = true;
          
          // Auto-play when modal opens
          this.$nextTick(() => {
            if (this.$refs.audioPlayer) {
              this.$refs.audioPlayer.play().catch(err => {
                console.error('Error playing audio:', err);
                this.audioError = 'فشل في تشغيل التسجيل. يرجى المحاولة مرة أخرى.';
              });
            }
          });
        } else {
          this.error = response.data?.result?.mesAr || 'فشل في الحصول على رابط التسجيل';
        }
      } catch (error) {
        console.error('Error fetching recording URL:', error);
        this.error = error.response?.data?.result?.mesAr || 'حدث خطأ أثناء جلب رابط التسجيل';
      }
    },
    closeAudioPlayer() {
      this.showAudioPlayer = false;
      this.currentRecordingUrl = null;
      this.currentSession = null;
      this.audioError = null;
      if (this.$refs.audioPlayer) {
        this.$refs.audioPlayer.pause();
        this.$refs.audioPlayer.currentTime = 0;
      }
    },
    handleAudioError() {
      this.audioError = 'فشل في تحميل التسجيل. قد يكون الملف غير متاح أو تالف.';
    },
    formatTime(time) {
      if (!time) return '-';
      const [hours, minutes] = time.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'م' : 'ص';
      const hour12 = hour % 12 || 12;
      return `${hour12}:${minutes} ${ampm}`;
    },
    formatDateTime(dateTime) {
      if (!dateTime) return '-';
      const date = new Date(dateTime);
      const hours = date.getHours();
      const minutes = date.getMinutes();
      const ampm = hours >= 12 ? 'م' : 'ص';
      const hour12 = hours % 12 || 12;
      return `${hour12}:${String(minutes).padStart(2, '0')} ${ampm}`;
    },
    getStatusClass(status) {
      const classes = {
        'scheduled': 'bg-yellow-100 text-yellow-800',
        'accepted': 'bg-blue-100 text-blue-800',
        'in_progress': 'bg-green-100 text-green-800',
        'completed': 'bg-gray-100 text-gray-800',
        'cancelled': 'bg-red-100 text-red-800',
      };
      return classes[status] || 'bg-gray-100 text-gray-800';
    },
  },
};
</script>

<style scoped>
/* Custom styles if needed */
</style>

